/**
 * The catalog of right-panel surface kinds: label, description, and the rules
 * about when each can be opened.
 *
 * This is the successor to `TAB_ORDER`. It stayed a separate list from
 * `RIGHT_PANEL_KINDS` for the same reason it always was — a kind needs a label,
 * a description and a gating rule that the store has no business carrying — but
 * it is now keyed by kind, so `rightPanelRegistry.test.ts` can assert coverage
 * by construction rather than by scanning an array.
 *
 * `instanced` is the new part. It is what the old model could not express:
 * browser sessions and files are peers you can have several of, while the rest
 * are one-per-thread. The plus menu and the store both read it.
 */
import type { RightPanelKind } from "../rightPanelSurface";

export interface RightPanelKindDefinition {
  readonly kind: RightPanelKind;
  readonly label: string;
  readonly description: string;
  readonly requiresServerThread: boolean;
  /** Several of this kind can be open at once, each addressing its own resource. */
  readonly instanced: boolean;
  /** Never offered in the plus menu; reachable only when something opens it. */
  readonly hidden?: boolean;
  readonly disabled?: boolean;
  readonly disabledReason?: string;
  readonly statusLabel?: string;
  /**
   * The tab can be renamed in place. The name is the user's, not the
   * resource's: a browser session stays on its page title in history, a
   * terminal stays on its session label, and the tab says what the user called
   * it.
   */
  readonly renamable?: boolean;
}

/**
 * Reason shown on draft threads for every surface that needs a persisted one.
 *
 * A draft becomes a server thread on the first send, so this is the concrete
 * action the user can take — not a restatement of the constraint.
 */
export const SERVER_THREAD_REQUIRED_REASON = "Available after your first message.";

/**
 * Strip order for the plus menu. Insertion order is the display order.
 */
export const RIGHT_PANEL_KIND_DEFINITIONS: Readonly<
  Record<RightPanelKind, RightPanelKindDefinition>
> = {
  preview: {
    kind: "preview",
    label: "Browser",
    description: "Open a URL or watch the agent browse.",
    requiresServerThread: false,
    instanced: true,
    renamable: true,
  },
  terminal: {
    kind: "terminal",
    label: "Terminal",
    description: "Run shell commands in the thread's workspace.",
    // Shells run on the runtime, which only a persisted server thread has.
    requiresServerThread: true,
    instanced: false,
    renamable: true,
  },
  files: {
    kind: "files",
    label: "Files",
    description: "Browse the thread's workspace tree.",
    // The tree lists the workspace through the runtime's directory-listing
    // RPC, which a draft thread has no runtime for.
    requiresServerThread: true,
    instanced: false,
    renamable: true,
  },
  display: {
    kind: "display",
    label: "Desktop",
    description: "Watch the whole runtime desktop, not just the browser.",
    // The stream comes from the runtime's own display, which only a persisted
    // server thread has.
    requiresServerThread: true,
    instanced: false,
  },
  canvas: {
    kind: "canvas",
    label: "Design",
    description: "Inspect and edit elements on a design canvas.",
    requiresServerThread: false,
    instanced: false,
    disabled: true,
    disabledReason: "Coming soon.",
    statusLabel: "Soon",
  },
  agents: {
    kind: "agents",
    label: "Agents",
    description: "Watch the workers this thread spawned.",
    // Workers are spawned threads with their own ids and branches, so there is
    // nothing to list until this thread itself is persisted.
    requiresServerThread: true,
    instanced: false,
  },
  diff: {
    kind: "diff",
    label: "Diff",
    description: "Review working-tree and turn diffs.",
    requiresServerThread: true,
    instanced: false,
  },
  file: {
    kind: "file",
    label: "File",
    description: "Read a file from the project.",
    // The panel reads through the workspace RPC, which a draft thread has no
    // runtime for.
    requiresServerThread: true,
    instanced: true,
    // Never offered as "add a File surface" — you open a file by naming one.
    hidden: true,
  },
  task: {
    kind: "task",
    label: "Task",
    description: "Track the CMMD task behind this thread.",
    requiresServerThread: true,
    instanced: false,
  },
  simulator: {
    kind: "simulator",
    label: "Simulator",
    description: "Run an iOS simulator on a connected Mac.",
    // A lease is allocated against a registered host and tracked in CMMD, so
    // there is nothing to show without a server thread.
    requiresServerThread: true,
    instanced: false,
  },
  plan: {
    kind: "plan",
    label: "Plan",
    description: "Follow active and proposed plan steps.",
    requiresServerThread: false,
    instanced: false,
    hidden: true,
  },
  "pull-request": {
    kind: "pull-request",
    label: "Pull request",
    description: "Review a pull request from a connected environment.",
    requiresServerThread: false,
    instanced: true,
    hidden: true,
  },
};
